import classNames from "classnames";

// How many columns the connected grid renders. Callers can force it via the
// `columns` prop; otherwise we pick a clean grid from the option count
// (2-3 fit one row, 4 -> 2x2, 5 -> single row of 5, 6+ -> 3-wide grid).
const deriveColumns = (count) => {
  if (count <= 3) return count;
  if (count === 4) return 2;
  if (count === 5) return 5;
  return 3;
};

/**
 * SegmentedToggle — a single-select control rendered as a connected grid of
 * segments (one rounded bordered container; the background shows through 1px
 * grid gaps as hairline dividers). Selected = solid brand-blue fill. This is the
 * shared visual language for the wound form's single-choice fields; see the
 * `.segmented-toggle` styles in `_form.scss`.
 *
 * Tri-state: clicking the already-selected segment clears the value back to
 * empty (optional fields only — required fields keep a value once set). The
 * resolved value (string `key`, or "" when cleared) is passed to `onSelect`, so
 * the stored data contract is always an option key.
 *
 * Optional `other` escape hatch: include an option in `options` whose key
 * matches `other.key` (default "other"); when it's selected, a reveal input
 * renders on its own line beneath the bar. Pass the input's wiring via
 * `other.input` ({ value, onChange, onBlur, type, placeholder, min, max, fullWidth, dataField }).
 *
 * @param {{key: string, label: string}[]} options
 * @param {string} value - currently selected key
 * @param {(value: string) => void} onSelect - receives the resolved key ("" when cleared)
 */
function SegmentedToggle({
  options = [],
  value,
  onSelect,
  columns,
  disabled = false,
  required = false,
  empty,
  ariaLabel,
  name,
  className,
  other = null,
}) {
  const cols = columns || deriveColumns(options.length || 1);
  // `empty` mirrors the required-but-unset red highlight used by inputs/selects.
  // Callers can pass it explicitly; otherwise derive it from required + value.
  const isEmpty = empty !== undefined ? empty : required && !value;
  const otherKey = other?.key || "other";
  const otherActive = !!other && value === otherKey;

  return (
    <>
      <div
        className={classNames("segmented-toggle", { empty: isEmpty }, className)}
        role="group"
        aria-label={ariaLabel}
        data-field={name || undefined}
        style={{ "--seg-cols": cols }}
      >
        {options.map((option) => {
          const selected = value === option.key;
          return (
            <button
              key={option.key}
              type="button"
              className={classNames("segmented-toggle__segment", { selected })}
              aria-pressed={selected}
              disabled={disabled}
              // Tri-state: clicking the selected segment clears it back to empty,
              // except required fields, which always keep a value once set.
              onClick={() => onSelect?.(selected ? (required ? option.key : "") : option.key)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {otherActive && other.input && (
        <input
          type={other.input.type || "text"}
          className={classNames("segmented-toggle__other-input", { "full-width": other.input.fullWidth })}
          placeholder={other.input.placeholder}
          min={other.input.min}
          max={other.input.max}
          disabled={disabled}
          value={other.input.value || ""}
          onChange={(e) => other.input.onChange?.(e)}
          onBlur={(e) => other.input.onBlur?.(e)}
          data-field={other.input.dataField || undefined}
        />
      )}
    </>
  );
}

export default SegmentedToggle;
